/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  DndContext,
  useSensors,
  PointerSensor,
  TouchSensor,
  useSensor,
  Active,
  DragEndEvent,
  MeasuringStrategy,
  UniqueIdentifier,
  DragOverEvent,
  MouseSensor,
} from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { faXmark, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FollowUpKeys } from '@gen2/api/follow-up/hooks';
import { TInviteRequest } from '@gen2/api/invite-requests/api';
import {
  InviteRequestsKeys,
  useFollowUpRequestReorder,
  useInviteRequestReorder,
  useTemplateRequestReorder,
} from '@gen2/api/invite-requests/hooks';
import { TemplateKeys } from '@gen2/api/templates/hooks';
import { queryClient } from '@gen2/config/queryClient';
import { useRouter } from '@gen2/hooks';
import { useToast } from '@gen2/hooks/useToast';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  IconButton,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { useIsFetching } from '@tanstack/react-query';
import randomstring from 'randomstring';
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import {
  MIN_TITLE_LENGTH,
  MAX_TITLE_LENGTH,
} from '../../invites/send-invites/requests/schema';
import { DroppableContainer } from './droppableContainer';
import {
  StyledDrawer,
  DrawerHeader,
  DrawerContent,
  Title,
  SubTitle,
  DrawerFooter,
  GroupContainer,
  GroupHeader,
  GroupName,
  ActionButtons,
  GroupContent,
  StyleTextField,
} from './shared-drawer.styles';
import { SortableRequestItemProps } from './sortable-request-list/sortable-request-item';
import { SortableRequestList } from './sortable-request-list/sortable-request-list';
import { useSharedDrawerStore } from './store';

type RequestType = 'invite' | 'template' | 'followup';

export interface RequestGroup {
  id: string;
  name: string;
  order: number;
  requests: SortableRequestItemProps[] | TInviteRequest[];
}

export const SharedDrawer = () => {
  const toast = useToast();
  const router = useRouter();
  const isFollowUp = router.pathname.includes('follow-up');
  const { isOpen, config, setIsOpen } = useSharedDrawerStore();
  const { inviteId, templateId, followUpId } = config.contentProps || {};
  const type = (config.contentProps?.type || 'invite') as RequestType;
  const items = useMemo(() => config.contentProps?.items || [], [config]);
  const itemsToGroup = useMemo(
    () => config.contentProps?.itemsToGroup || [],
    [config],
  );

  const [active, setActive] = useState<Active | null>(null);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  // lots of computations here
  type Items = Record<string, SortableRequestItemProps[]>;
  const initialFakeItems: Items = useMemo(() => {
    const result: Items = {
      ungroup: [], // default ungrouped requests list
    };

    //handle ungrouped requests
    if (items && Array.isArray(items) && items.length > 0) {
      result['ungroup'] = items.map((item) => ({
        id: String(item.id),
        title: item.title,
      }));
    }

    //handle grouped requests
    // const contentProps = config.contentProps as ContentProps | undefined;
    if (itemsToGroup && Array.isArray(itemsToGroup)) {
      itemsToGroup.forEach((group: RequestGroup) => {
        if (group && group.requests && Array.isArray(group.requests)) {
          result[group.id] = group.requests.map(
            (request: SortableRequestItemProps | TInviteRequest) => ({
              id: String(request.id),
              title: request.title,
            }),
          );
        }
      });
    }

    return result;
  }, [items, itemsToGroup]);

  const initialGroupNames = useMemo(() => {
    if (itemsToGroup && itemsToGroup.length > 0) {
      return itemsToGroup.reduce(
        (acc, group) => {
          acc[group.id] = group.name;
          return acc;
        },
        {} as Record<string, string>,
      );
    }
    return {};
  }, [itemsToGroup]);

  const [groupNames, setGroupNames] =
    useState<Record<string, string>>(initialGroupNames);

  const [groupNameErrors, setGroupNameErrors] = useState<
    Record<string, string>
  >({});
  const [fakeItems, setFakeItems] = useState<Items>(initialFakeItems);

  const [clonedItems, setClonedItems] = useState<Items | null>(null);

  const initialContainers = useMemo(() => {
    let containers: string[] = [];
    if (itemsToGroup && Array.isArray(itemsToGroup)) {
      containers = itemsToGroup.map((group) => group.id);
    }
    containers.push('ungroup'); // ungroup always last
    return containers;
  }, [itemsToGroup]);

  const [containers, setContainers] = useState(initialContainers);

  // Reset items when drawer opens
  useEffect(() => {
    if (isOpen) {
      setFakeItems(initialFakeItems);
      setContainers(initialContainers);
      if (itemsToGroup && itemsToGroup.length > 0) {
        setGroupNames(
          itemsToGroup.reduce(
            (acc, group) => {
              acc[group.id] = group.name;
              return acc;
            },
            {} as Record<string, string>,
          ),
        );
      }
    }
  }, [initialContainers, initialFakeItems, isOpen, itemsToGroup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setActiveId(null);
      setActive(null);
      setClonedItems(null);
    };
  }, []);

  const recentlyMovedToNewContainer = useRef(false);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor),
  );

  // Memoize fetching states
  const isFetchingInviteRequests = useIsFetching({
    queryKey: [InviteRequestsKeys.getInviteRequests],
  });
  const isFetchingTemplateRequests = useIsFetching({
    queryKey: [TemplateKeys.getTemplateRequests],
  });
  const isFetchingFollowUpRequests = useIsFetching({
    queryKey: [FollowUpKeys.getFollowUps],
  });

  const isFetchingFollowUpRequest = useIsFetching({
    queryKey: [FollowUpKeys.getFollowUp],
  });

  const isFetchingInviteRequestGroups = useIsFetching({
    queryKey: [InviteRequestsKeys.getInviteRequestGroups],
  });

  const isFetchingTemplateRequestGroups = useIsFetching({
    queryKey: [TemplateKeys.getTemplateRequestGroups],
  });

  const {
    mutate: mutateInviteRequestReorder,
    isLoading: isInviteRequestReorderUpdating,
  } = useInviteRequestReorder();
  const {
    mutate: mutateTemplateRequestReorder,
    isLoading: isTemplateRequestReorderUpdating,
  } = useTemplateRequestReorder();
  const {
    mutate: mutateFollowUpRequestReorder,
    isLoading: isFollowUpRequestReorderLoading,
  } = useFollowUpRequestReorder();

  // Memoize loading state
  const isLoading = useMemo(
    () =>
      isInviteRequestReorderUpdating ||
      isTemplateRequestReorderUpdating ||
      isFollowUpRequestReorderLoading ||
      isFetchingInviteRequests > 0 ||
      isFetchingTemplateRequests > 0 ||
      isFetchingFollowUpRequests > 0 ||
      isFetchingFollowUpRequest > 0 ||
      isFetchingInviteRequestGroups > 0 ||
      isFetchingTemplateRequestGroups > 0,
    [
      isInviteRequestReorderUpdating,
      isTemplateRequestReorderUpdating,
      isFollowUpRequestReorderLoading,
      isFetchingInviteRequests,
      isFetchingTemplateRequests,
      isFetchingFollowUpRequests,
      isFetchingFollowUpRequest,
      isFetchingInviteRequestGroups,
      isFetchingTemplateRequestGroups,
    ],
  );

  const handleClose = useCallback(
    (event: object, reason: string) => {
      if (reason === 'backdropClick') {
        return;
      }
      setActiveId(null);
      setActive(null);
      setClonedItems(null);
      setContainers(initialContainers);
      setIsOpen(false);
      setSelectedIds([]);
    },
    [setIsOpen, initialContainers],
  );

  const handleMutationSuccess = useCallback(
    async (type: RequestType) => {
      switch (type) {
        case 'invite':
          await queryClient.invalidateQueries({
            queryKey: [InviteRequestsKeys.getInviteRequests],
          });
          await queryClient.invalidateQueries({
            queryKey: [InviteRequestsKeys.getInviteRequestGroups],
          });
          break;
        case 'template':
          await queryClient.invalidateQueries({
            queryKey: [TemplateKeys.getTemplateRequests],
          });
          await queryClient.invalidateQueries({
            queryKey: [TemplateKeys.getTemplateRequestGroups],
          });
          break;
        case 'followup':
          await queryClient.invalidateQueries({
            queryKey: isFollowUp
              ? [FollowUpKeys.getFollowUp]
              : [FollowUpKeys.getFollowUps],
          });
          break;
      }
      setIsOpen(false);
      setActiveId(null);
      setActive(null);
      setClonedItems(null);
      setContainers(initialContainers);
      setFakeItems(initialFakeItems);
    },
    [isFollowUp, setIsOpen, initialContainers, initialFakeItems],
  );

  const handleMutationError = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error: any) => {
      toast.show({
        text: 'Failed to save order',
        variant: 'error',
      });
    },
    [toast],
  );

  const getNextContainerId = () => {
    return randomstring.generate(7);
  };

  const getNextGroupName = () => {
    const existingGroups = containers.filter((id) => id !== 'ungroup');
    const groupNumbers = existingGroups.map((id) => {
      const name = groupNames[id] || '';
      const match = name.match(/Group (\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    });
    const nextNumber = Math.max(0, ...groupNumbers) + 1;
    return `Group ${nextNumber}`;
  };

  const handleCreateGroup = () => {
    const newContainerId = getNextContainerId();
    const newGroupName = getNextGroupName();
    setContainers((containers) => {
      // Find the index of 'ungroup'
      const ungroupIndex = containers.indexOf('ungroup');
      // Insert the new container before 'ungroup'
      return [
        ...containers.slice(0, ungroupIndex),
        newContainerId,
        ...containers.slice(ungroupIndex),
      ];
    });
    setFakeItems((items) => ({
      ...items,
      [newContainerId]: [],
    }));
    setGroupNames((prev) => ({
      ...prev,
      [newContainerId]: newGroupName,
    }));
  };

  const validateGroupName = (name: string) => {
    if (!name.trim()) {
      return 'Group name is required';
    }
    if (name.length < MIN_TITLE_LENGTH) {
      return `Group name must be at least ${MIN_TITLE_LENGTH} characters`;
    }
    if (name.length > MAX_TITLE_LENGTH - 1) {
      return `Group name must not be greater than ${
        MAX_TITLE_LENGTH - 1
      } characters`;
    }
    return '';
  };

  const handleGroupNameChange = (containerId: string, value: string) => {
    setGroupNames((prev) => ({
      ...prev,
      [containerId]: value,
    }));

    const error = validateGroupName(value);
    setGroupNameErrors((prev) => ({
      ...prev,
      [containerId]: error,
    }));
  };

  const handleSave = useCallback(() => {
    // Validate all group names before saving
    const errors: Record<string, string> = {};
    let hasErrors = false;

    Object.entries(groupNames).forEach(([containerId, name]) => {
      const error = validateGroupName(name);
      if (error) {
        errors[containerId] = error;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setGroupNameErrors(errors);
      toast.show({
        text: 'Please fix group name errors before saving',
        variant: 'error',
      });
      return;
    }

    // Check for empty groups
    const emptyGroups = containers
      .filter((id) => id !== 'ungroup')
      .filter((id) => !fakeItems[id] || fakeItems[id].length === 0);

    if (emptyGroups.length > 0) {
      toast.show({
        text: 'Please remove empty groups before saving',
        variant: 'error',
      });
      return;
    }

    // Get the order of containers first (excluding ungroup)
    const containerOrder = containers.filter((id) => id !== 'ungroup');

    // Process grouped items
    const requestGroups = containerOrder.map((containerId, groupIndex) => {
      const groupItems = (fakeItems[containerId] || []).map((item, index) => ({
        id: String(item.id),
        order: index + 1,
      }));

      return {
        id: containerId,
        name: groupNames[containerId] || `Group ${groupIndex + 1}`,
        order: groupIndex + 1,
        requests: groupItems,
      };
    });

    // Process ungrouped items
    const ungroupedRequests = (fakeItems['ungroup'] || []).map(
      (item, index) => ({
        id: String(item.id),
        order: index + 1,
      }),
    );

    const payload = {
      request_groups: requestGroups,
      requests: ungroupedRequests,
    };

    if (requestGroups.length === 0 && ungroupedRequests.length === 0) {
      setIsOpen(false);
      return;
    }

    switch (type) {
      case 'invite':
        mutateInviteRequestReorder(
          { inviteId: inviteId!, data: payload },
          {
            onSuccess: () => handleMutationSuccess('invite'),
            onError: (error: unknown) => handleMutationError(error),
          },
        );
        break;
      case 'template':
        mutateTemplateRequestReorder(
          { templateId: templateId!, data: payload },
          {
            onSuccess: () => handleMutationSuccess('template'),
            onError: (error: unknown) => handleMutationError(error),
          },
        );
        break;
      case 'followup':
        mutateFollowUpRequestReorder(
          {
            inviteId: inviteId!,
            followUpId: followUpId!,
            data: payload,
          },
          {
            onSuccess: () => handleMutationSuccess('followup'),
            onError: (error: unknown) => handleMutationError(error),
          },
        );
        break;
    }
  }, [
    groupNames,
    containers,
    fakeItems,
    type,
    toast,
    setIsOpen,
    mutateInviteRequestReorder,
    inviteId,
    mutateTemplateRequestReorder,
    templateId,
    mutateFollowUpRequestReorder,
    followUpId,
    handleMutationSuccess,
    handleMutationError,
  ]);

  const findContainer = useCallback(
    (id: UniqueIdentifier) => {
      if (id in fakeItems) {
        return id;
      }

      return Object.keys(fakeItems).find((key) =>
        fakeItems[key] && Array.isArray(fakeItems[key])
          ? fakeItems[key].some((item) => item.id === id)
          : false,
      );
    },
    [fakeItems],
  );

  const initialContainer = useMemo(
    () => (activeId ? findContainer(activeId) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeId],
  );

  const filterItems = (items: SortableRequestItemProps[] | undefined) => {
    if (!items || !Array.isArray(items)) {
      return [];
    }

    const validItems = items.filter(Boolean);

    if (!activeId) {
      return validItems;
    }

    return validItems.filter(
      (item: SortableRequestItemProps) =>
        item.id === activeId || !selectedIds.includes(String(item.id)),
    );
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    try {
      const overId = over?.id;

      if (!overId || active.id in fakeItems) {
        return;
      }

      const overContainer = findContainer(overId);
      const activeContainer = findContainer(active.id);

      if (!overContainer || !activeContainer) {
        return;
      }

      if (activeContainer !== overContainer) {
        setFakeItems((items) => {
          const activeItems = items[activeContainer] || [];
          const overItems = items[overContainer] || [];
          const overIndex = overItems.findIndex((item) => item.id === overId);
          const activeIndex = activeItems.findIndex(
            (item) => item.id === active.id,
          );

          let newIndex: number;

          if (overId in items) {
            newIndex = overItems.length + 1;
          } else {
            const isBelowOverItem =
              over &&
              active.rect.current.translated &&
              active.rect.current.translated.top >
                over.rect.top + over.rect.height;

            const modifier = isBelowOverItem ? 1 : 0;

            newIndex =
              overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
          }

          recentlyMovedToNewContainer.current = true;

          // Get all selected items including the active one
          const ids = selectedIds.length
            ? [active.id, ...selectedIds.filter((id) => id !== active.id)]
            : [active.id];

          return {
            ...items,
            [activeContainer]: items[activeContainer].filter(
              (item) => !ids.includes(item.id),
            ),
            [overContainer]: [
              ...(overItems || []).slice(0, newIndex),
              ...ids
                .map((id) =>
                  (items[activeContainer] || []).find(
                    (item) => item && item.id === id,
                  ),
                )
                .filter(
                  (item): item is SortableRequestItemProps =>
                    item !== undefined,
                )
                .filter(Boolean),
              ...(overItems || []).slice(newIndex),
            ],
          };
        });
      }
    } catch (error) {
      console.error('Error in drag over:', error);
    }
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    try {
      if (active.id in fakeItems && over?.id) {
        setContainers((containers) => {
          const activeIndex = containers.indexOf(String(active.id));
          const overIndex = containers.indexOf(String(over.id));

          return arrayMove(containers, activeIndex, overIndex);
        });
      }

      const activeContainer = findContainer(active.id);
      const overId = over?.id;

      if (!activeContainer || !overId || !initialContainer) {
        setActiveId(null);
        setSelectedIds([]);
        return;
      }

      // Get all selected items including the active one
      const ids = selectedIds.length
        ? [active.id, ...selectedIds.filter((id) => id !== active.id)]
        : [active.id];

      const overContainer = findContainer(overId);

      if (overContainer) {
        const overItems = filterItems(fakeItems[overContainer]);
        if (!overItems) {
          setActiveId(null);
          setSelectedIds([]);
          return;
        }

        const overIndex = overItems.findIndex(
          (item: SortableRequestItemProps) => item?.id === overId,
        );
        const activeIndex = overItems.findIndex(
          (item: SortableRequestItemProps) => item?.id === active.id,
        );
        const newItems = arrayMove(overItems, activeIndex, overIndex);
        const newActiveIndex = newItems.findIndex(
          (item: SortableRequestItemProps) => item?.id === active.id,
        );

        setFakeItems((items) => ({
          ...items,
          [initialContainer]: (items[initialContainer] || []).filter(
            (item: SortableRequestItemProps) => !ids.includes(item.id),
          ),
          [activeContainer]: (items[activeContainer] || []).filter(
            (item: SortableRequestItemProps) => !ids.includes(item.id),
          ),
          [overContainer]: [
            ...(newItems || []).slice(0, newActiveIndex + 1),
            ...ids
              .filter((id) => id !== active.id && id != null)
              .map((id) =>
                (items[activeContainer] || []).find(
                  (item: SortableRequestItemProps) => item && item.id === id,
                ),
              )
              .filter(
                (item): item is SortableRequestItemProps => item !== undefined,
              ),
            ...(newItems || []).slice(newActiveIndex + 1),
          ].filter(Boolean),
        }));
      }

      setActiveId(null);
      setSelectedIds([]);
    } catch (error) {
      console.error('Error in drag end:', error);
      setActiveId(null);
      setSelectedIds([]);
    }
  };

  const onDragCancel = () => {
    if (clonedItems) {
      setFakeItems(clonedItems);
    }

    setActiveId(null);
    setClonedItems(null);
  };

  const handleRemoveGroup = (containerId: string) => {
    setFakeItems((items) => {
      const deletedGroupItems = items[containerId] || [];
      return {
        ...items,
        ungroup: [...items['ungroup'], ...deletedGroupItems],
        [containerId]: [],
      };
    });
    setContainers((containers) =>
      containers.filter((id) => id !== containerId),
    );
    setGroupNames((prev) => {
      const { [containerId]: _, ...rest } = prev;
      return rest;
    });
  };

  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const handleSelect = (id: UniqueIdentifier | string) => {
    setSelectedIds((selectedIds) => {
      if (selectedIds.includes(String(id))) {
        return selectedIds.filter((value) => value !== String(id));
      }

      if (
        !selectedIds.length ||
        findContainer(id) !== findContainer(selectedIds[0])
      ) {
        return [String(id)];
      }

      return selectedIds.concat(String(id));
    });
  };

  const [showUngroupConfirm, setShowUngroupConfirm] = useState(false);

  // Check if any selected items are in groups
  const hasSelectedItemsInGroups = useMemo(() => {
    return selectedIds.some((id) => {
      const container = findContainer(id);
      return container && container !== 'ungroup' && fakeItems[container];
    });
  }, [selectedIds, findContainer, fakeItems]);

  const handleUngroup = () => {
    if (selectedIds.length === 0) return;

    // If multiple items are selected, show confirmation
    if (selectedIds.length > 1) {
      setShowUngroupConfirm(true);
      return;
    }

    // Otherwise proceed with ungrouping
    performUngroup();
  };

  const performUngroup = () => {
    setFakeItems((items) => {
      const newItems = { ...items };
      const itemsToMove: SortableRequestItemProps[] = [];

      // First collect all items to move
      selectedIds.forEach((id) => {
        const container = findContainer(id);
        if (
          container &&
          container !== 'ungroup' &&
          Array.isArray(items[container])
        ) {
          const itemToMove = (items[container] || []).find(
            (item) => item.id === id,
          );
          if (itemToMove) {
            itemsToMove.push(itemToMove);
          }
        }
      });

      // Then remove all selected items from their original containers
      Object.keys(newItems).forEach((containerId) => {
        if (containerId !== 'ungroup') {
          newItems[containerId] = (newItems[containerId] || []).filter(
            (item) => !selectedIds.includes(String(item.id)),
          );
        }
      });

      // Finally add all collected items to ungroup
      newItems['ungroup'] = [...(newItems['ungroup'] || []), ...itemsToMove];

      // Clean up empty groups
      Object.keys(newItems).forEach((containerId) => {
        if (
          containerId !== 'ungroup' &&
          (!newItems[containerId] || newItems[containerId].length === 0)
        ) {
          delete newItems[containerId];
          setContainers((containers) =>
            containers.filter((id) => id !== containerId),
          );
          setGroupNames((prev) => {
            const { [containerId]: _, ...rest } = prev;
            return rest;
          });
        }
      });

      return newItems;
    });

    setSelectedIds([]);
    setShowUngroupConfirm(false);
  };

  return (
    <>
      <StyledDrawer
        anchor="right"
        open={isOpen}
        onClose={handleClose}
        data-cy="shared-drawer"
        componentsProps={{
          backdrop: {
            style: {
              opacity: 0.5,
            },
          },
        }}
      >
        <DrawerHeader>
          <Stack direction="row" alignItems="center" gap={3}>
            <IconButton
              onClick={(event) => handleClose(event, 'close')}
              size="small"
              color="tonalLight"
              data-cy="close-drawer-btn"
              aria-label="close drawer"
            >
              <FontAwesomeIcon icon={faXmark} />
            </IconButton>
            <Box>
              <Title>Organize Requests</Title>
              <SubTitle>Drag and drop to reorder requests.</SubTitle>
            </Box>
          </Stack>
          <LoadingButton
            color="primary"
            variant="contained"
            data-cy="save"
            onClick={handleSave}
            loading={isLoading}
          >
            Save
          </LoadingButton>
        </DrawerHeader>
        <DndContext
          sensors={sensors}
          measuring={{
            droppable: {
              strategy: MeasuringStrategy.Always,
            },
          }}
          onDragStart={({ active }) => {
            setSelectedIds((selected) => {
              return selected.includes(active.id.toString()) ? selected : [];
            });
            setActiveId(active.id);
            setClonedItems(fakeItems);
          }}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={onDragCancel}
        >
          <SortableContext items={[...containers]}>
            <DrawerContent data-cy="drawer-content">
              {/* group container */}
              {containers.map((containerId) => (
                <DroppableContainer
                  key={containerId}
                  id={containerId}
                  items={fakeItems[containerId] || []}
                >
                  {containerId !== 'ungroup' ? (
                    <GroupContainer>
                      <GroupHeader>
                        <GroupName>
                          <StyleTextField
                            defaultValue={
                              groupNames[containerId] ||
                              `Group ${Object.keys(groupNames).length + 1}`
                            }
                            type="text"
                            fullWidth
                            data-cy={`group-name-${containerId}`}
                            error={!!groupNameErrors[containerId]}
                            helperText={groupNameErrors[containerId]}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              handleGroupNameChange(containerId, e.target.value)
                            }
                          />
                        </GroupName>
                        <ActionButtons>
                          <IconButton
                            color="tonalLight"
                            size="small"
                            onClick={() => handleRemoveGroup(containerId)}
                          >
                            <FontAwesomeIcon icon={faTrashCan} />
                          </IconButton>
                          <div
                            id={`${containerId}-handle`}
                            style={{ cursor: 'grab' }}
                          />
                        </ActionButtons>
                      </GroupHeader>
                      <GroupContent>
                        {fakeItems[containerId] &&
                        filterItems(fakeItems[containerId]).length > 0 ? (
                          <SortableRequestList
                            items={filterItems(fakeItems[containerId]) || []}
                            type={type}
                            active={active}
                            onSelect={handleSelect}
                            selectedIds={selectedIds}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            textAlign="center"
                          >
                            Drag requests here to add them to this group
                          </Typography>
                        )}
                      </GroupContent>
                    </GroupContainer>
                  ) : (
                    <SortableRequestList
                      items={fakeItems[containerId] || []}
                      type={type}
                      active={active}
                      onSelect={handleSelect}
                      selectedIds={selectedIds}
                    />
                  )}
                </DroppableContainer>
              ))}
            </DrawerContent>
            <DrawerFooter>
              <Button
                color="ghost"
                variant="outlined"
                data-cy="deselect"
                disabled={selectedIds.length === 0}
                onClick={() => setSelectedIds([])}
              >
                Deselect
              </Button>
              <Stack direction="row" gap={2}>
                <Button
                  color="tertiary"
                  variant="outlined"
                  data-cy="ungroup"
                  onClick={handleUngroup}
                  disabled={!hasSelectedItemsInGroups}
                >
                  Ungroup
                </Button>
                <LoadingButton
                  color="secondary"
                  variant="contained"
                  data-cy="create-group"
                  onClick={handleCreateGroup}
                >
                  Create Group
                </LoadingButton>
              </Stack>
            </DrawerFooter>
          </SortableContext>
        </DndContext>
      </StyledDrawer>

      <Dialog
        open={showUngroupConfirm}
        onClose={() => setShowUngroupConfirm(false)}
        data-cy="ungroup-confirm-dialog"
      >
        <DialogTitle>Ungroup Requests</DialogTitle>
        <DialogContent>
          Are you sure you want to ungroup {selectedIds.length} selected
          request(s)?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowUngroupConfirm(false)}
            color="ghost"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button onClick={performUngroup} color="primary" variant="contained">
            Ungroup
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
